// import { useRouterScroll } from '@moxy/next-router-scroll';
import { useCallback } from 'react';
import { useSWRInfinite } from 'swr';
import { Meta, ResponseContainer, clientSession, publicSession } from './apiService';

export interface UsePaginationProps<T = any> {
  initialUrl?: string;
  initialData?: { data: T[]; meta: Meta };
  /**
   * The viewer the pages are requested as, with their token: an id of theirs (e.g. user and profile ids) that
   * keeps their pages cached apart, so a guest's or another viewer's are never reused. Without it, anonymously.
   */
  viewer?: string;
}

type Page = ResponseContainer<any[], Meta>;

// A viewer's page is requested silently: one that fails to load is just not shown, as with the public session.
const fetchPage = (url: string, viewer?: string) => (
  (viewer ? clientSession.get<never, Page>(url, { handledByCaller: true }) : publicSession.get<never, Page>(url))
    .then(({ data, meta }) => ({ data, meta }))
);

export default function usePagination<T = any>({ initialData, initialUrl, viewer }: UsePaginationProps<T>) {
  const {
    data: rawData,
    error,
    setSize,
    isValidating,
  } = useSWRInfinite(
    (pageIndex, prevData: { data: T[]; meta: Meta }) => {
      // console.log({ pageIndex, prevData });
      const url = pageIndex === 0
        ? (initialData ? (initialData.meta?.next_url || initialData?.meta?.videos_next_url) : initialUrl)
        : (prevData?.meta?.next_url || prevData?.meta?.videos_next_url);
      if (!url) return null;
      return viewer ? [url, viewer] : url;
    },
    fetchPage,
    // Past the initial data no page is loaded until asked, so a revalidation (e.g. the key changing once
    // the viewer is known) has none to fetch.
    { initialData: initialData ? [] : undefined, initialSize: initialData ? 0 : 1 },
  );

  const dataSwr = initialData ? [initialData, ...(Array.isArray(rawData) ? rawData : [])] : rawData;

  const data = dataSwr?.reduce((a: T[], v) => [...a, ...(v.data || [])], []) ?? [];
  const loading = !dataSwr && !error;
  const remainingItems = (dataSwr ? dataSwr[dataSwr.length - 1].meta.remaining_items_count : initialData?.meta.remaining_items_count) ?? 0;
  const handleMore = useCallback(() => setSize((prev) => (rawData ? rawData.length + 1 : prev + 1)), [setSize, rawData]);

  const hasMore = !!(dataSwr?.[dataSwr.length - 1]?.meta?.next_url || dataSwr?.[dataSwr.length - 1]?.meta?.videos_next_url);
  const getInfiniteScrollProps = () => ({
    hasMore,
    next: handleMore,
    dataLength: dataSwr?.length ?? 0,
    loader: <p>...</p>,
  });

  return {
    data,
    loading,
    error,
    remainingItems,
    isValidating,
    hasMore,
    handleMore,
    getInfiniteScrollProps,
  };
}
